<template>
  <div class="user-profile-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#c44f0b"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#172238" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between w-100">
        <span class="white--text">{{ msg }}</span>
        <v-btn icon small text color="white" @click="showsnackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- 1. Top Bar: Page Title & Global Actions -->
    <div class="page-top-bar d-flex align-center justify-space-between mb-5 flex-wrap">
      <div>
        <PageBreadcrumb :items="[{ text: 'Business Profile' }]" class="mb-1" />
        <p class="page-subtitle">Manage your jewellery enterprise identity, legal registrations, and banking details.</p>
      </div>
      <div class="d-flex align-center mt-2 mt-sm-0">
        <!-- Edit Profile Trigger -->
        <button class="action-btn-primary mr-3" @click="openEditDialog">
          <v-icon size="16" color="#ffffff" class="mr-2">mdi-pencil-outline</v-icon>
          <span>Edit Profile</span>
        </button>

        <!-- GST Tax Switch Pill -->
        <div class="gst-toggle-pill d-flex align-center">
          <v-icon size="16" :color="stat ? '#16a34a' : '#94a3b8'" class="mr-2">mdi-shield-check-outline</v-icon>
          <span class="gst-label mr-2">GST Mode</span>
          <v-switch
            v-model="stat"
            inset
            dense
            color="#16a34a"
            hide-details
            class="ma-0 pa-0 gst-switch"
            @change="changeStat"
          ></v-switch>
        </div>
      </div>
    </div>

    <!-- 2. Unified Hero Profile Header Card -->
    <div class="profile-hero-card mb-5 pa-5 pa-sm-6">
      <div class="d-flex align-center justify-space-between flex-wrap gap-4">
        <!-- Left: Avatar + Identity + Meta details -->
        <div class="d-flex align-center flex-wrap gap-4">
          <div class="hero-avatar-box">
            <span class="avatar-monogram">{{ getInitials }}</span>
          </div>
          <div class="hero-identity">
            <div class="d-flex align-center flex-wrap gap-2 mb-1">
              <h2 class="jewellery-business-name">{{ userData.jewelleryName || 'Jewellery Enterprise' }}</h2>
              <span class="verification-badge">
                <v-icon size="14" color="#16a34a" class="mr-1">mdi-check-decagram</v-icon>
                Verified Account
              </span>
            </div>
            <div class="hero-meta-strip d-flex align-center flex-wrap gap-3 mt-1">
              <span class="meta-item" v-if="userData.location">
                <v-icon size="14" color="#9a6b27" class="mr-1">mdi-map-marker-outline</v-icon>
                {{ userData.location }}
              </span>
              <span class="meta-item" v-if="userData.phone">
                <v-icon size="14" color="#9a6b27" class="mr-1">mdi-phone-outline</v-icon>
                {{ userData.phone }}
              </span>
              <span class="meta-item" v-if="userData.email">
                <v-icon size="14" color="#9a6b27" class="mr-1">mdi-email-outline</v-icon>
                {{ userData.email }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right: Tax Status & Admin Tag -->
        <div class="hero-tags-col d-flex flex-column align-end">
          <div class="tax-status-pill mb-1" :class="{ 'tax-active': stat, 'tax-inactive': !stat }">
            <span class="status-dot mr-1"></span>
            <span>{{ stat ? 'GST Registered' : 'Non-GST Billing' }}</span>
          </div>
          <span class="admin-username-label">Admin: <strong class="text-dark">{{ userData.username || 'nandhu' }}</strong></span>
        </div>
      </div>
    </div>

    <!-- 3. Professional Structured 3-Column Info Grid -->
    <div class="profile-cards-grid">
      <!-- Section 1: Legal & Registration Credentials -->
      <div class="info-section-card">
        <div class="card-header d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="header-icon-box orange-box mr-3">
              <v-icon size="18" color="#9a6b27">mdi-file-certificate-outline</v-icon>
            </div>
            <h3 class="card-title">Registration Details</h3>
          </div>
          <span class="card-chip chip-orange">Compliance</span>
        </div>
        <div class="card-body-fields">
          <div class="field-item">
            <span class="field-label">GSTIN</span>
            <span class="field-value font-mono">{{ userData.GSTIN || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">TAN Number</span>
            <span class="field-value font-mono">{{ userData.TAN || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">BIS License</span>
            <span class="field-value">{{ userData.BisLicense || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">State & Code</span>
            <span class="field-value">{{ userData.stateandCode || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Insurance Policy No</span>
            <span class="field-value">{{ userData.policyNo || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Company ID</span>
            <span class="field-value font-mono">{{ userData.companyId || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Section 2: Bank & Account Details -->
      <div class="info-section-card">
        <div class="card-header d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="header-icon-box blue-box mr-3">
              <v-icon size="18" color="#2563eb">mdi-bank-outline</v-icon>
            </div>
            <h3 class="card-title">Bank Details</h3>
          </div>
          <span class="card-chip chip-blue">Financial</span>
        </div>
        <div class="card-body-fields">
          <div class="field-item">
            <span class="field-label">Bank Name</span>
            <span class="field-value">{{ userData.bankName || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Account Number</span>
            <span class="field-value font-mono">{{ userData.accountNo || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">IFSC Code</span>
            <span class="field-value font-mono">{{ userData.IFSCCode || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Branch</span>
            <span class="field-value">{{ userData.branch || '—' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">PAN Card No</span>
            <span class="field-value font-mono">{{ userData.pancardNo || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Section 3: Billing & Operational Settings -->
      <div class="info-section-card">
        <div class="card-header d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="header-icon-box green-box mr-3">
              <v-icon size="18" color="#16a34a">mdi-cog-outline</v-icon>
            </div>
            <h3 class="card-title">Operations & Billing</h3>
          </div>
          <span class="card-chip chip-green">Configuration</span>
        </div>
        <div class="card-body-fields">
          <div class="field-item">
            <span class="field-label">Making Charge Format</span>
            <span class="field-value">{{ userData.billMakingChargeFormat || 'Percentage' }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Discount Approval Limit</span>
            <span class="field-value">
              {{ userData.discountApprovalThreshold ? '₹ ' + formatNumber(userData.discountApprovalThreshold) : 'No Review Required (0)' }}
            </span>
          </div>
          <div class="field-item">
            <span class="field-label">System Role</span>
            <span class="field-value">{{ formatRole(userData.mainRole) }}</span>
          </div>
          <div class="field-item">
            <span class="field-label">Registered Store Name</span>
            <span class="field-value">{{ userData.jewelleryName || '—' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Modern Professional Edit Profile Modal -->
    <v-dialog v-model="editdialogue" max-width="840px" persistent>
      <v-card class="edit-dialog-card pa-6">
        <!-- Dialog Header -->
        <div class="dialog-header d-flex align-center justify-space-between mb-5">
          <div class="d-flex align-center">
            <div class="dialog-icon-box mr-3">
              <v-icon size="20" color="#c44f0b">mdi-square-edit-outline</v-icon>
            </div>
            <div>
              <h2 class="dialog-title">Edit Business Profile</h2>
              <p class="dialog-subtitle">Update enterprise credentials, legal details, and banking information.</p>
            </div>
          </div>
          <v-btn icon small text @click="editdialogue = false">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider class="mb-5"></v-divider>

        <!-- Form Content -->
        <v-form ref="profileForm" v-model="formValid">
          <!-- Form Section 1: General Info -->
          <div class="form-section-title">
            <v-icon small color="#c44f0b" class="mr-1">mdi-store-outline</v-icon>
            <span>General Information</span>
          </div>
          <v-row dense class="mb-4">
            <v-col cols="12" md="6">
              <label class="input-label">Jewellery Store Name *</label>
              <v-text-field
                v-model="curItem.jewelleryName"
                outlined
                dense
                placeholder="e.g. Nandhu Jewellers"
                :rules="[rules.required]"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Location / City</label>
              <v-text-field
                v-model="curItem.location"
                outlined
                dense
                placeholder="e.g. Kanjirappally"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Contact Phone *</label>
              <v-text-field
                v-model="curItem.phone"
                outlined
                dense
                placeholder="10-digit mobile number"
                :rules="phoneRules"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Email Address *</label>
              <v-text-field
                v-model="curItem.email"
                outlined
                dense
                placeholder="store@email.com"
                :rules="[rules.email]"
                class="modern-input"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Form Section 2: Registration & Tax -->
          <div class="form-section-title">
            <v-icon small color="#9a6b27" class="mr-1">mdi-file-certificate-outline</v-icon>
            <span>Tax & Legal Registrations</span>
          </div>
          <v-row dense class="mb-4">
            <v-col cols="12" md="4">
              <label class="input-label">GSTIN *</label>
              <v-text-field
                v-model="curItem.GSTIN"
                outlined
                dense
                placeholder="15-digit GSTIN"
                :rules="[rules.required]"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">TAN Number</label>
              <v-text-field
                v-model="curItem.TAN"
                outlined
                dense
                placeholder="TAN Number"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">BIS License</label>
              <v-text-field
                v-model="curItem.BisLicense"
                outlined
                dense
                placeholder="BIS License No"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">Insurance Policy No</label>
              <v-text-field
                v-model="curItem.policyNo"
                outlined
                dense
                placeholder="Policy Number"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">Company ID</label>
              <v-text-field
                v-model="curItem.companyId"
                outlined
                dense
                placeholder="Company ID"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">State & Code</label>
              <v-text-field
                v-model="curItem.stateandCode"
                outlined
                dense
                placeholder="e.g. Kerala - 32"
                class="modern-input"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Form Section 3: Banking -->
          <div class="form-section-title">
            <v-icon small color="#c44f0b" class="mr-1">mdi-bank-outline</v-icon>
            <span>Banking Details</span>
          </div>
          <v-row dense class="mb-4">
            <v-col cols="12" md="4">
              <label class="input-label">Bank Name</label>
              <v-text-field
                v-model="curItem.bankName"
                outlined
                dense
                placeholder="e.g. State Bank of India"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">Account Number</label>
              <v-text-field
                v-model="curItem.accountNo"
                outlined
                dense
                placeholder="Account Number"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <label class="input-label">IFSC Code</label>
              <v-text-field
                v-model="curItem.IFSCCode"
                outlined
                dense
                placeholder="IFSC Code"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Branch Name</label>
              <v-text-field
                v-model="curItem.branch"
                outlined
                dense
                placeholder="Branch"
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">PAN Card No</label>
              <v-text-field
                v-model="curItem.pancardNo"
                outlined
                dense
                placeholder="PAN Number"
                class="modern-input"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Form Section 4: Operational Preferences -->
          <div class="form-section-title">
            <v-icon small color="#c44f0b" class="mr-1">mdi-cog-outline</v-icon>
            <span>Operational & Billing Preferences</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <label class="input-label">Making Charge Format</label>
              <v-select
                v-model="curItem.billMakingChargeFormat"
                :items="['Percentage', 'Amount']"
                outlined
                dense
                class="modern-input"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <label class="input-label">Discount Approval Limit (₹, 0 = off)</label>
              <v-text-field
                v-model="curItem.discountApprovalThreshold"
                type="number"
                outlined
                dense
                placeholder="0 = no review required"
                class="modern-input"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>

        <v-divider class="my-5"></v-divider>

        <!-- Dialog Actions -->
        <div class="d-flex align-center justify-end">
          <button class="dialog-cancel-btn mr-3" @click="editdialogue = false">
            Cancel
          </button>
          <button class="dialog-save-btn" @click="editUser">
            Save Changes
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import store from "./../../store";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 3000,
      msg: null,
      stat: false,
      editdialogue: false,
      curItem: {},
      formValid: true,
      rules: {
        required: (value) => !!value || "Required field.",
        email: (value) => {
          if (!value) return true;
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid email format.";
        },
      },
      phoneRules: [
        (v) => !!v || "Phone is required",
        (v) => (v && v.length === 10) || "Phone must be 10 digits",
        (v) => /^\d+$/.test(v) || "Must be numeric",
      ],
    };
  },
  computed: {
    userData() {
      return this.$store.state.userData || {};
    },
    getInitials() {
      const name = (this.userData && this.userData.jewelleryName) || (this.userData && this.userData.username) || "JB";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
  },
  mounted() {
    const rawStat = localStorage.getItem("GSTstatus");
    try {
      this.stat = JSON.parse(rawStat);
    } catch (e) {
      this.stat = rawStat === "true" || rawStat === true;
    }
  },
  methods: {
    formatNumber(val) {
      if (!val) return "0";
      return Number(val).toLocaleString("en-IN");
    },
    formatRole(role) {
      if (!role) return "Jewellery Admin";
      if (role === "JewelleryAdmin") return "Jewellery Administrator";
      if (role === "SuperAdmin") return "Super Administrator";
      return role;
    },
    openEditDialog() {
      this.curItem = JSON.parse(JSON.stringify(this.userData || {}));
      this.editdialogue = true;
    },
    changeStat() {
      this.appLoading = true;
      axios({
        url: "/change/taxtype",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: localStorage.getItem("id"),
          taxType: this.stat,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            const stat1 = response.data.data.taxType;
            localStorage.setItem("GSTstatus", stat1);
            store.commit("GSTstatus", stat1);
            this.msg = response.data.msg || "Tax status updated";
            this.showsnackbar = true;
            setTimeout(() => {
              location.reload();
            }, 600);
          } else {
            this.msg = response.data.msg || "Failed to update tax status";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    editUser() {
      if (this.$refs.profileForm && !this.$refs.profileForm.validate()) {
        return;
      }
      this.appLoading = true;
      axios({
        url: "/user/adminedit",
        method: "POST",
        data: {
          id: this.curItem._id || this.curItem.id || localStorage.getItem("id"),
          location: this.curItem.location,
          email: this.curItem.email,
          jewelleryName: this.curItem.jewelleryName,
          phone: this.curItem.phone,
          GSTIN: this.curItem.GSTIN,
          TAN: this.curItem.TAN,
          stateandCode: this.curItem.stateandCode,
          BisLicense: this.curItem.BisLicense,
          policyNo: this.curItem.policyNo,
          companyId: this.curItem.companyId,
          bankName: this.curItem.bankName,
          accountNo: this.curItem.accountNo,
          IFSCCode: this.curItem.IFSCCode,
          branch: this.curItem.branch,
          pancardNo: this.curItem.pancardNo,
          discountApprovalThreshold: this.curItem.discountApprovalThreshold,
          billMakingChargeFormat: this.curItem.billMakingChargeFormat,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.editdialogue = false;
            this.msg = response.data.msg || "Profile updated successfully";
            this.showsnackbar = true;
            if (response.data.data) {
              store.commit("userData", response.data.data);
            }
            setTimeout(() => {
              location.reload();
            }, 600);
          } else {
            this.msg = response.data.msg || "Failed to save profile";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.user-profile-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #172238;
  text-align: left;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 4px 32px;
}

/* Page Top Header */
.page-title {
  font-size: 24px;
  font-weight: 800;
  color: #172238;
  margin: 0;
  line-height: 1.2;
  text-align: left;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  text-align: left;
}

.action-btn-primary {
  display: inline-flex;
  align-items: center;
  background: #c44f0b;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(196, 79, 11, 0.25);
}

.action-btn-primary:hover {
  background: #a83f05;
  box-shadow: 0 4px 12px rgba(196, 79, 11, 0.35);
  transform: translateY(-1px);
}

.gst-toggle-pill {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.gst-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
}

/* Hero Profile Card */
.profile-hero-card {
  background: linear-gradient(135deg, #ffffff 0%, #fffdfa 100%);
  border: 1px solid #ebd8c2;
  border-radius: 16px;
  box-shadow: 0 4px 18px rgba(184, 106, 36, 0.04);
  text-align: left;
}

.hero-avatar-box {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: linear-gradient(135deg, #c44f0b 0%, #8c3b0d 100%);
  border: 3px solid #ffffff;
  box-shadow: 0 4px 14px rgba(196, 79, 11, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-monogram {
  font-size: 26px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 1px;
}

.hero-identity {
  text-align: left;
}

.jewellery-business-name {
  font-size: 22px;
  font-weight: 800;
  color: #172238;
  margin: 0;
  line-height: 1.2;
  text-align: left;
  letter-spacing: -0.01em;
}

.verification-badge {
  display: inline-flex;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
}

.hero-meta-strip {
  font-size: 13px;
  color: #64748b;
  text-align: left;
}

.meta-item {
  display: inline-flex;
  align-items: center;
}

.tax-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.tax-status-pill.tax-active {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.tax-status-pill.tax-inactive {
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.admin-username-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* 3-Column Info Cards Grid */
.profile-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  text-align: left;
}

@media (max-width: 1100px) {
  .profile-cards-grid {
    grid-template-columns: 1fr;
  }
}

.info-section-card {
  background: #ffffff;
  border: 1px solid #ebd8c2;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  text-align: left;
}

.info-section-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  border-color: #dfcaa8;
}

.card-header {
  padding-bottom: 12px;
  border-bottom: 1px solid #f1ece5;
  margin-bottom: 14px;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #172238;
  margin: 0;
  text-align: left;
}

.header-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orange-box {
  background: #fff4e8;
  border: 1px solid #f3dec9;
}

.blue-box {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.green-box {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}

.card-chip {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 6px;
}

.chip-orange {
  background: #fff4e8;
  color: #c44f0b;
}

.chip-blue {
  background: #eff6ff;
  color: #2563eb;
}

.chip-green {
  background: #f0fdf4;
  color: #16a34a;
}

/* Field Key-Value Items */
.card-body-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

.field-item {
  display: flex;
  flex-direction: column;
  text-align: left;
  background: #fafaf9;
  border: 1px solid #f1ece5;
  border-radius: 8px;
  padding: 8px 12px;
}

.field-label {
  font-size: 11px;
  font-weight: 600;
  color: #78716c;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: left;
}

.field-value {
  font-size: 13.5px;
  font-weight: 600;
  color: #172238;
  word-break: break-all;
  text-align: left;
  line-height: 1.3;
}

.font-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  letter-spacing: 0.3px;
  color: #0f172a;
}

/* Edit Dialog Styles */
.edit-dialog-card {
  border-radius: 16px !important;
  background: #ffffff !important;
}

.dialog-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fff4e8;
  border: 1px solid #f3dec9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-title {
  font-size: 18px;
  font-weight: 800;
  color: #172238;
  margin: 0;
}

.dialog-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin: 2px 0 0;
}

.form-section-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #8c6227;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.input-label {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.modern-input :deep(.v-input__control .v-input__slot) {
  min-height: 40px !important;
  border-radius: 8px !important;
  font-size: 13.5px !important;
}

.dialog-cancel-btn {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.dialog-cancel-btn:hover {
  background: #e2e8f0;
}

.dialog-save-btn {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  border: none;
  border-radius: 10px;
  padding: 8px 22px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28);
}

.dialog-save-btn:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38);
  transform: translateY(-1px);
}
</style>
